import React, { useEffect, useRef, useState } from 'react';
import './style.scss';
import NftImage from '../../../general/nft/image';
import { IArrayState } from '../../../../hooks/useArrayState';
import {
  API_GetOwnNftResponse,
  API_OwnNft,
  MintTxStatuses,
  nftDetailsToOwn
} from 'shared';
import localStorage from '../../../../utils/localStorage';
import fetcher from '../../../../utils/fetcher';
import useAlert from '../../../../contexts/alert';
import FightPickNftCarousel from './carousel';
import FightPickControls from './controls';
import FightPickDescription from './description';

const getInitialIndex = (nfts: API_OwnNft[]): number => {
  const storedToken = localStorage.getPrefNftToken();
  if (storedToken) {
    const index = nfts.findIndex((nft) => nft.token === storedToken);
    if (index >= 0) return index;
  }
  if (nfts.length >= 3) return 1;
  return 0;
};

interface Props {
  nfts: IArrayState<API_OwnNft>;
  reload: () => Promise<API_GetOwnNftResponse>;
}

const FightPick = ({ nfts, reload }: Props) => {
  const alert = useAlert();
  const firstLoad = useRef(true);
  const [activeNftIndex, setActiveNftIndex] = useState<number>(0);

  useEffect(() => {
    if (firstLoad.current) {
      setActiveNftIndex(getInitialIndex(nfts.value));
    }
  });

  const updateNftStatus = (token: string, stopLoading?: () => any) => {
    fetcher.nft
      .updateStatus(token)
      .then((response) => {
        if ('needUpdate' in response) {
          reload()
            .then((response) => nfts.setValue(response))
            .finally(stopLoading);
          return;
        }
        if (stopLoading) stopLoading();
        if (response.status === MintTxStatuses.PENDING) {
          return alert.show('Nothing has changed so far');
        }
        if (response.nft) {
          alert.show('Successfully Minted!');
          nfts.replaceOne(
            (nft) => nft.token === token,
            () => nftDetailsToOwn(response.nft)
          );
        }
      })
      .catch((err) => {
        alert.showError(err);
        if (stopLoading) stopLoading();
      });
  };

  const removeFailedNft = (token: string, stopLoading?: () => any) => {
    fetcher.nft
      .removeFailed(token)
      .then(() => {
        if (activeNftIndex >= nfts.value.length - 1) {
          handleActiveIndexChange(0);
        }
        nfts.removeOne((nft) => nft.token === token);
      })
      .catch((err) => {
        alert.showError(err);
        if (stopLoading) stopLoading();
      });
  };

  const handleActiveIndexChange = (newIndex: number) => {
    setActiveNftIndex(newIndex);
    const nft = nfts.value[newIndex];
    if (nft) localStorage.setPrefNftToken(nft.token);
  };

  const selectedNft = nfts.value[activeNftIndex];

  return (
    <div className='fight-pick'>
      <div className='fight-pick-top'>
        <div className='fight-pick-top-left'>
          <FightPickNftCarousel
            nfts={nfts.value}
            activeIndex={activeNftIndex}
            setActiveIndex={handleActiveIndexChange}
          />
        </div>
        <div className='fight-pick-top-center'>
          <NftImage
            nft={selectedNft}
            className='fight-pick-nft-image'
            disabled={selectedNft.pending || selectedNft.failed}
          />
        </div>
        <div className='fight-pick-top-right'>
          <FightPickDescription nft={selectedNft} />
          <FightPickControls
            nft={selectedNft}
            updateStatus={(stopLoading) =>
              updateNftStatus(selectedNft.token, stopLoading)
            }
            removeNft={(stopLoading) =>
              removeFailedNft(selectedNft.token, stopLoading)
            }
            className='fight-pick-nft-controls'
          />
        </div>
      </div>
      <div className='fight-pick-bottom'>
        <FightPickControls
          nft={selectedNft}
          updateStatus={(stopLoading) =>
            updateNftStatus(selectedNft.token, stopLoading)
          }
          removeNft={(stopLoading) =>
            removeFailedNft(selectedNft.token, stopLoading)
          }
          className='fight-pick-nft-controls'
          fullWidth
        />
        <FightPickNftCarousel
          nfts={nfts.value}
          activeIndex={activeNftIndex}
          setActiveIndex={handleActiveIndexChange}
          horizontal
        />
      </div>
    </div>
  );
};

export default FightPick;
